import { InfiniteData } from '@tanstack/react-query'
import queryString from 'query-string'
import { OrganizationRoles } from 'src/enums'
import { PaginationResponse } from 'src/models/pagination.models'
import { UserListModel, UserListResponse } from 'src/models/user.models'
import { orgQueryKeys } from 'src/queries/queries.constants'
import {
  ExtendBaseInfiniteQueryOptions,
  getNextExtendPageParam,
  useExtendInfiniteQuery,
} from 'src/queries/queries.utils'

export interface GetOrgMembersResponse {
  pagination: PaginationResponse
  users: UserListResponse[]
}

interface GetOrgmembersDataModel {
  members: UserListModel[]
  page: number
}

const selectOrgMembersListModel = (
  response: InfiniteData<GetOrgMembersResponse, number>,
): GetOrgmembersDataModel => {
  const members = response.pages.flatMap((page) =>
    page.users.map((member) => new UserListModel(member)),
  )
  return {
    members: members,
    page: response.pageParams.length - 1,
  }
}

interface QueryOrgMembersOptions
  extends ExtendBaseInfiniteQueryOptions<
    GetOrgMembersResponse,
    number,
    GetOrgmembersDataModel
  > {
  orgId: string | undefined
  queryParams: GetOrgMembersRequest
}

export interface GetOrgMembersRequest {
  search?: string
  count?: number
  showDeactivatedUsers?: boolean
  isInDepartment?: boolean
  organizationRoles?: OrganizationRoles[]
  renderMetrics?: boolean
}

export const useQueryOrgMembers = ({
  orgId,
  queryParams: {
    search,
    count = 50,
    isInDepartment,
    organizationRoles,
    renderMetrics,
  },
  enabled = true,
  ...rest
}: QueryOrgMembersOptions) => {
  const queryParams: GetOrgMembersRequest = {
    search,
    count,
    isInDepartment,
    organizationRoles,
    renderMetrics,
  }
  return useExtendInfiniteQuery({
    queryKey: orgQueryKeys.members(orgId, queryParams),
    queryUrl: ({ pageParam }) =>
      `/organizations/${orgId}/members?${queryString.stringify({ ...queryParams, page: pageParam })}`,
    initialPageParam: 0,
    getNextPageParam: (lastPage, _, __) =>
      getNextExtendPageParam(lastPage.pagination),
    enabled: enabled && orgId !== undefined,
    select: selectOrgMembersListModel,
    ...rest,
  })
}
